"use client";

import {
  createContext,
  use,
  useCallback,
  useId,
  useLayoutEffect,
  useMemo,
  useState,
  type AriaAttributes,
} from "react";

type FieldMessageKind = "description" | "error" | "suggestion";

interface FieldMessage {
  id: string;
  kind: FieldMessageKind;
}

interface FieldControlContextValue {
  controlId: string;
  describedBy: ReadonlyArray<string>;
  invalid: boolean;
  suggesting: boolean;
  registerMessage: (message: FieldMessage) => () => void;
}

export const FieldControlContext = createContext<FieldControlContextValue | null>(
  null,
);

export function useFieldControlContextValue(
  explicitControlId: string | undefined,
): FieldControlContextValue {
  const generatedControlId = useId();
  const [messages, setMessages] = useState<ReadonlyArray<FieldMessage>>([]);
  const registerMessage = useCallback((message: FieldMessage) => {
    setMessages((current) => [...current, message]);
    return () =>
      setMessages((current) =>
        current.filter((entry) => entry.id !== message.id),
      );
  }, []);
  const controlId = explicitControlId ?? generatedControlId;
  return useMemo(
    () => ({
      controlId,
      describedBy: [
        ...messages.filter((entry) => entry.kind === "description"),
        ...messages.filter((entry) => entry.kind === "error"),
      ].map((entry) => entry.id),
      invalid: messages.some((entry) => entry.kind === "error"),
      suggesting: messages.some((entry) => entry.kind === "suggestion"),
      registerMessage,
    }),
    [controlId, messages, registerMessage],
  );
}

export function useFieldMessage(id: string, kind: FieldMessageKind, rendered: boolean) {
  const field = use(FieldControlContext);
  const registerMessage = field?.registerMessage;
  useLayoutEffect(() => {
    if (!registerMessage || !rendered) return;
    return registerMessage({ id, kind });
  }, [registerMessage, id, kind, rendered]);
}

type AriaInvalid = AriaAttributes["aria-invalid"];

function joinIdReferences(
  explicit: string | undefined,
  registered: ReadonlyArray<string>,
): string | undefined {
  const references = new Set([
    ...(explicit?.split(/\s+/).filter(Boolean) ?? []),
    ...registered,
  ]);
  return references.size > 0 ? [...references].join(" ") : undefined;
}

interface FieldControlProps {
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: AriaInvalid;
}

export function useFieldControl<Props extends FieldControlProps>(
  props: Props,
): Props {
  const field = use(FieldControlContext);
  if (!field) return props;
  return {
    ...props,
    id: props.id ?? field.controlId,
    "aria-describedby": joinIdReferences(
      props["aria-describedby"],
      field.describedBy,
    ),
    "aria-invalid": props["aria-invalid"] ?? (field.invalid || undefined),
  };
}
